// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import React, { useContext, createContext, useState, useEffect } from 'react'
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser'
import { InteractionType, PublicClientApplication } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { findIana } from 'windows-iana'

import config from './config'
import {
  getUserCalendars,
  getUserWeekCalendar,
  getEventDetailsById,
  createEvent
} from 'utils/api/GraphService'
import dayjs from 'dayjs'
import {
  cacheOtherCalendarsEvents,
  createGeneralGroupIfNeeded
} from '../CalendarSyncProdivder'
import { MSCalendar } from 'types/ms_outlook'
import { outlookPrefix } from 'utils/constants'
import Logger from 'services/Logger'
import { Event, PatternedRecurrence } from '@microsoft/microsoft-graph-types'
import {
  DocType,
  EventDoc,
  EventRecurrence,
  EventReminder,
  EventStatus,
  RECURRENCE_UNIT,
  UserSettingsDoc
} from 'types/api/changes'
import { allOutlookCalendars } from 'services/db/Queries/GroupQueries'
import Database from 'services/db/Database'
import { otherCalendarEvents } from 'services/db/Queries/Event'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { darken } from '@mui/material'
import { ConferenceData, ICalendar } from 'types/google_events'
import { CurrentConvertedCalendars } from 'utils/google_events'
import { UserSettings } from 'services/db/Queries/UserSettingsQueries'
import { DayOfWeek } from 'microsoft-graph'
import { getDefaultTimezone } from 'index'
import { eventLengthInDays } from 'utils/event'

// <AppContextSnippet>
export interface AppUser {
  displayName?: string
  email?: string
  avatar?: string
  timeZone?: string
  timeFormat?: string
}

export interface AppError {
  message: string
  debug?: string
}

type OutlookContext = {
  user?: AppUser
  error?: AppError
  signIn?: Function
  signOut?: Function
  displayError?: Function
  clearError?: Function
  authProvider?: AuthCodeMSALBrowserAuthenticationProvider
  syncCalendars?: Function
  getEventDetails?: Function
  addEvent?: Function
}

const outlookConext = createContext<OutlookContext>({
  user: undefined,
  error: undefined,
  signIn: undefined,
  signOut: undefined,
  displayError: undefined,
  clearError: undefined,
  authProvider: undefined,
  syncCalendars: undefined,
  getEventDetails: undefined,
  addEvent: undefined
})

export function useOutlookContext(): OutlookContext {
  return useContext(outlookConext)
}

interface ProvideOutlookContextProps {
  children: React.ReactNode
}

export default function ProvideOutlookContext({
  children
}: ProvideOutlookContextProps) {
  const auth = useProvideOutlookContext()
  return (
    <outlookConext.Provider value={auth}>{children}</outlookConext.Provider>
  )
}
// </AppContextSnippet>

function useProvideOutlookContext() {
  const msal = useMsal()
  const [user, setUser] = useState<AppUser | undefined>(undefined)
  const [error, setError] = useState<AppError | undefined>(undefined)

  function markCalendarsAsProblematic() {
    allOutlookCalendars().then((calendars) => {
      if (calendars.length > 0) {
        calendars.forEach((calendar) => {
          if (calendar.SyncProblem !== '1') {
            Database.write(async () => {
              calendar.update((local) => {
                local.SyncProblem = '1'
              })
            })
          }
        })
      }
    })
  }

  useEffect(() => {
    if (msal.accounts.length === 0) {
      Logger.red('No MS accounts')
      // in case we do not have active MS accounts, we need to check local converted calendars
      markCalendarsAsProblematic()
    } else {
      // If there are accounts, check if the token is expired
      const account = msal.accounts[0]
      const silentRequest = {
        scopes: ['calendars.readwrite'], // replace with your actual scopes
        account: account
      }
      msal.instance.initialize().then(() => {
        msal.instance
          .acquireTokenSilent(silentRequest)
          .then((response) => {
            const expiryDate = response.expiresOn

            if (expiryDate && expiryDate < new Date()) {
              markCalendarsAsProblematic()
            }
          })
          .catch((err) => {
            console.error('Error acquiring token', err)
            markCalendarsAsProblematic()
          })
      })
    }
  }, [msal.accounts])

  const displayError = (message: string, debug?: string) => {
    setError({ message, debug })
  }

  const clearError = () => {
    setError(undefined)
  }

  // <AuthProviderSnippet>
  // Used by the Graph SDK to authenticate API calls
  const authProvider = new AuthCodeMSALBrowserAuthenticationProvider(
    msal.instance as PublicClientApplication,
    {
      account: msal.instance.getActiveAccount()!,
      scopes: config.scopes,
      interactionType: InteractionType.Popup
    }
  )
  // </AuthProviderSnippet>

  // <SignInSnippet>
  const signIn = async () => {
    await msal.instance.loginPopup({
      scopes: config.scopes,
      prompt: 'select_account'
    })

    await syncCalendars()
  }
  // </SignInSnippet>

  // <SignOutSnippet>
  const signOut = async () => {
    await msal.instance.logoutPopup()
    setUser(undefined)
  }
  // </SignOutSnippet>

  const syncCalendars = async () => {
    console.log('Outlook:', msal.instance.getActiveAccount())

    if (!msal.instance.getActiveAccount()) return
    getUserCalendars(authProvider).then((calendars) => {
      console.log('Calendars: ', calendars)
      let groupId: string | undefined = undefined
      calendars.forEach(async (calendar: MSCalendar) => {
        if (calendar.isDefaultCalendar) {
          groupId = outlookPrefix + calendar.owner?.address
        }
      })

      calendars.forEach(async (calendar: MSCalendar) => {
        if (calendar.isDefaultCalendar) {
          LocalStorage.set(
            LocalStorageKey.OUTLOOK_DEFAULT_CALENDAR,
            calendar.owner.address
          )
          createGeneralGroupIfNeeded(
            outlookPrefix,
            calendar.owner.address,
            calendar.hexColor.length > 0 ? calendar.hexColor : '#3293eb',
            calendar.owner.address,
            'Outlook Calendar'
          )
        }

        const startDateTime = LocalStorage.get(
          LocalStorageKey.VISIBLE_FROM
        ) as string
        const endDateTime = LocalStorage.get(
          LocalStorageKey.VISIBLE_TO
        ) as string

        const eventsForCalendar = await getUserWeekCalendar(
          authProvider,
          getDefaultTimezone(),
          calendar.isDefaultCalendar ? undefined : calendar.id,
          startDateTime,
          endDateTime
        )

        if (groupId)
          processEventsFromOutlook(
            eventsForCalendar,
            calendar,
            groupId,
            startDateTime,
            endDateTime
          )
        else {
          Logger.red(
            'Outlook:ProcessEvents',
            'Default group for outlook not found'
          )
        }
      })

      calendars.forEach((calendar: MSCalendar) => {
        let iCalendar: ICalendar = {
          title: calendar.name
        }

        if (
          CurrentConvertedCalendars.find(
            (calendarFromServer) => calendarFromServer.id === calendar.id
          ) === undefined
        ) {
          iCalendar.id = calendar.id
          iCalendar.title = calendar.isDefaultCalendar
            ? calendar.owner.address
            : calendar.name
          iCalendar.name = calendar.isDefaultCalendar
            ? calendar.owner.address
            : calendar.name
          iCalendar.backgroundColor =
            calendar.hexColor.length > 0 ? calendar.hexColor : '#3293eb'
          iCalendar.accessRole = calendar.canEdit ? 'owner' : 'reader'
          iCalendar.prefix = outlookPrefix
          iCalendar.primary = calendar.isDefaultCalendar
          iCalendar.primaryCalendarEmail =
            LocalStorage.get(LocalStorageKey.OUTLOOK_DEFAULT_CALENDAR) ?? ''
          CurrentConvertedCalendars.push(iCalendar)
        }
      })
    })
  }

  const getEventDetails = async (eventId: string) => {
    return getEventDetailsById(authProvider, eventId)
  }

  async function addEvent(event: Event, calendarId: string): Promise<Event> {
    Logger.blue('Outlook: Add event', event)
    return createEvent(authProvider, event, calendarId)
  }

  return {
    user,
    error,
    signIn,
    signOut,
    displayError,
    clearError,
    authProvider,
    syncCalendars,
    getEventDetails,
    addEvent
  }
}

async function processEventsFromOutlook(
  events: Event[],
  calendar: MSCalendar,
  groupId: string,
  startDateTime: string,
  endDateTime: string
) {
  const convertedOutlookEvents = events.map((event: Event) => {
    const tfmEvent: EventDoc = {
      ...outlookEventToTfmEvent(event),
      GroupID: outlookPrefix + calendar.owner?.address,
      ThirdPartyID: calendar.id,
      Color: darken(
        calendar.hexColor.length > 0 ? calendar.hexColor : '#3293eb',
        0.15
      )
    }

    return tfmEvent
  })

  const fromDateISO = dayjs(startDateTime).toISOString()
  const toDateISO = dayjs(endDateTime).subtract(1, 'day').toISOString()

  const existing = (
    await otherCalendarEvents(groupId, fromDateISO, toDateISO)
  ).filter((event) => event.ThirdPartyID === calendar.id)

  cacheOtherCalendarsEvents(events, existing, convertedOutlookEvents, undefined)
}

export function outlookEventToTfmEvent(event: Event): EventDoc {
  let reminders: EventReminder[] = []
  if (event.isReminderOn) {
    const reminder: EventReminder = {
      isOn: '1',
      isRelativeReminder: '1',
      offset: event.reminderMinutesBeforeStart ?? 60,
      AlertTime: ''
    }
    reminders.push(reminder)
  }

  let tz = getDefaultTimezone()

  if (event.originalStartTimeZone && event.originalStartTimeZone !== 'UTC') {
    const result = findIana(event.originalStartTimeZone)

    if (result.length > 0) {
      tz = result[0]
    } else {
      tz = event.originalStartTimeZone
    }
  }

  let conferenceData: ConferenceData | undefined = undefined

  if (event.onlineMeeting && event.onlineMeetingUrl) {
    conferenceData = {
      entryPoints: [
        {
          entryPointType: 'video',
          uri: event.onlineMeetingUrl
        }
      ],
      conferenceSolution: {
        name: 'Skype',
        iconUri:
          'https://s3.amazonaws.com/res.twentyfour.me/ImagesInTheApps/skype.svg'
      }
    }
  }

  let start = dayjs(event.start?.dateTime).unix()
  let end = dayjs(event.end?.dateTime).unix()

  if (event.isAllDay) {
    start = dayjs(event.start?.dateTime)
      .tz('UTC')
      .add(12, 'hour')
      .startOf('day')
      .unix()
    end = dayjs(event.end?.dateTime)
      .tz('UTC')

      .unix()
  }

  return {
    _id: (event as any).occurrenceId ? (event as any).occurrenceId : event.id,
    local_id: (event as any).occurrenceId
      ? (event as any).occurrenceId
      : event.id,
    Type: DocType.REGULAR_EVENT,
    SupplementaryGroupsIDs: [],
    OwnerID:
      event.organizer?.emailAddress?.address ??
      LocalStorage.get(LocalStorageKey.USER_ID) ??
      '',
    Status: EventStatus.ACTIVE,
    TimeZoneNameID: event.isAllDay ? 'UTC' : tz,
    LastUpdate: `${dayjs(event.lastModifiedDateTime).unix()}`,
    Rank: `${dayjs(event?.createdDateTime).unix()}`,
    Text: event.subject || 'No title',
    StartDate: start.toString(),
    EndDate: end.toString(),
    AllDay: event.isAllDay ? '1' : '0',
    Recurrence: mapOutlookRecurrenceToGroupcal(event.recurrence),
    Notes: generateTfmNote(event),
    Reminder: reminders,
    ParentTaskID: event.seriesMasterId ?? '',
    Location: mapOutlookLocationToGroupcal(event.locations?.[0]),
    canAddAttendees: event.isOrganizer ? '1' : '0',
    RequestConfirmation: !event.isOrganizer ? '1' : '0',
    ConferenceData: conferenceData,
    attendees: event.attendees?.map((attendee) => {
      return {
        email: attendee.emailAddress?.address ?? '',
        displayName: attendee.emailAddress?.name ?? '',
        self:
          attendee.emailAddress?.address ===
          LocalStorage.get(LocalStorageKey.OUTLOOK_DEFAULT_CALENDAR),
        responseStatus:
          attendee.emailAddress?.address ===
          LocalStorage.get(LocalStorageKey.OUTLOOK_DEFAULT_CALENDAR)
            ? outlookAttendeeStatusToGeneral(event.responseStatus?.response)
            : outlookAttendeeStatusToGeneral(attendee.status?.response)
      }
    })
  }
}

function mapOutlookRecurrenceToGroupcal(
  recurrence: PatternedRecurrence | undefined | null
): EventRecurrence | undefined {
  if (recurrence) {
    const unit = outlookRecurrenceTypeToGroupcal(recurrence.pattern?.type)
    const interval = recurrence.pattern?.interval

    if (unit === undefined || interval === undefined) {
      return undefined
    }
    return {
      Unit: Number(unit),
      Interval: interval,
      EndDate:
        recurrence.range?.type === 'noEnd'
          ? undefined
          : dayjs(recurrence.range?.endDate).unix().toString()
    }
  }

  return undefined
}

function outlookRecurrenceTypeToGroupcal(type: string | undefined | null) {
  switch (type) {
    case 'daily':
      return '1'
    case 'weekly':
      return '2'
    case 'absoluteMonthly':
      return '3'
    case 'relativeMonthly':
      return '3'
    case 'absoluteYearly':
      return '4'
    case 'relativeYearly':
      return '4'
    default:
      return undefined
  }
}

function groupcalRecurrenceTypeToOutlook(type: string | undefined | null) {
  switch (type) {
    case '1':
      return 'daily'
    case '2':
      return 'weekly'
    case '3':
      return 'absoluteMonthly'
    case '4':
      return 'absoluteYearly'
    default:
      return undefined
  }
}

function generateTfmNote(
  event: Event
): import('types/api/changes').EventNote[] | undefined {
  if (event.bodyPreview && event.bodyPreview.length > 0) {
    return [
      {
        Note: event.bodyPreview,
        NoteID: dayjs().unix(),
        Status: 1,
        FilePath: ''
      }
    ]
  }
}

function mapOutlookLocationToGroupcal(
  location:
    | import('@microsoft/microsoft-graph-types').NullableOption<
        import('@microsoft/microsoft-graph-types').Location
      >
    | undefined
): import('types/api/changes').EventLocation | undefined {
  if (location?.displayName) {
    return {
      Address: location.address
        ? location.displayName +
          ' (' +
          (location.address?.street?.toString() ?? '') +
          ', ' +
          (location.address?.city?.toString() ?? '') +
          ', ' +
          (location.address?.state?.toString() ?? '') +
          ', ' +
          (location.address?.postalCode?.toString() ?? '') +
          ', ' +
          (location.address?.countryOrRegion?.toString() ?? '') +
          ')'
        : location.displayName ?? '',
      Lat: location.coordinates?.latitude?.toString(),
      Long: location.coordinates?.longitude?.toString(),
      FilteredLocation: 'null'
    }
  }

  return undefined
}

export async function tfmEventToOutlook(event: EventDoc): Promise<Event> {
  let recurrence: PatternedRecurrence | undefined = undefined
  let userSettings = await UserSettings()
  if (event.Recurrence && (event.Recurrence as EventRecurrence)) {
    recurrence = tfmRecurrenceToOutlookRecurrence(event, userSettings)
  }

  let tz = event.TimeZoneNameID

  let attendees: import('@microsoft/microsoft-graph-types').Attendee[] = []

  let responseStatus = undefined

  if (event.attendees) {
    attendees = event.attendees.map((attendee) => {
      const outlookAttendee = {
        emailAddress: {
          address: attendee.email,
          name: attendee.displayName
        },
        status: {
          response: generalStatusToOutlook(attendee.responseStatus),
          time: dayjs().toISOString()
        }
      }

      return outlookAttendee
    })

    responseStatus = {
      response: generalStatusToOutlook(
        event.attendees.find((attendee) => attendee.self)?.responseStatus
      ),
      time: dayjs().toISOString()
    }
  }
  Logger.blue('Outlook: Attendees', event._id)

  const evenLen = eventLengthInDays(event)

  return {
    id: event._id,
    subject: event.Text,
    originalStartTimeZone: event.AllDay === '1' ? getDefaultTimezone() : tz,
    originalEndTimeZone: event.AllDay === '1' ? getDefaultTimezone() : tz,
    start: {
      dateTime: (event.AllDay === '1'
        ? dayjs.unix(Number(event.StartDate)).startOf('day')
        : dayjs(Number(event.StartDate) * 1000).tz(tz)
      ).format('YYYY-MM-DDTHH:mm:ss'),
      timeZone: event.AllDay === '1' ? getDefaultTimezone() : tz
    },
    end: {
      dateTime: (event.AllDay === '1'
        ? dayjs.unix(Number(event.EndDate)).startOf('day').add(1, 'day')
        : dayjs(Number(event.EndDate) * 1000).tz(tz)
      ).format('YYYY-MM-DDTHH:mm:ss'),
      timeZone: event.AllDay === '1' ? getDefaultTimezone() : tz
    },

    isAllDay: event.AllDay === '1',

    location: {
      displayName: event.Location?.Address,
      address: {
        street: event.Location?.Address
      },
      coordinates: {
        latitude: Number(event.Location?.Lat),
        longitude: Number(event.Location?.Long)
      }
    },
    responseStatus: responseStatus,
    attendees: attendees,
    body: event.Notes
      ? {
          content: event.Notes?.[0]?.Note,
          contentType: 'text'
        }
      : undefined,
    recurrence: recurrence,
    reminderMinutesBeforeStart: event.Reminder?.[0].offset,
    isReminderOn: event.Reminder?.[0] !== undefined
  }
}

export function generalStatusToOutlook(
  responseStatus: string | undefined | null
):
  | import('microsoft-graph').NullableOption<
      import('microsoft-graph').ResponseType
    >
  | undefined {
  switch (responseStatus) {
    case 'accepted':
      return 'accepted'
    case 'declined':
      return 'declined'
    case 'tentative':
      return 'tentativelyAccepted'
    default:
      return 'none'
  }
}

export function outlookAttendeeStatusToGeneral(
  responseStatus?: import('microsoft-graph').ResponseType | null
): string {
  switch (responseStatus) {
    case 'accepted':
      return 'accepted'
    case 'declined':
      return 'declined'
    case 'tentativelyAccepted':
      return 'tentative'
    default:
      return 'needsAction'
  }
}

function isTokenExpired(token: string) {
  const base64Url = token.split('.')[1]
  if (base64Url === undefined) return true
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join('')
  )

  const { exp } = JSON.parse(jsonPayload)
  const currentTimestamp = Math.floor(new Date().getTime() / 1000)

  return currentTimestamp > exp
}

function tfmRecurrenceToOutlookRecurrence(
  event: EventDoc,
  userSettings: UserSettingsDoc
): PatternedRecurrence | undefined {
  const recur = event.Recurrence as EventRecurrence
  const type = groupcalRecurrenceTypeToOutlook(recur.Unit?.toString())
  const recurrence: PatternedRecurrence = {}

  const eventStartTime = dayjs.unix(Number(event.StartDate))
  const dayOfMonth = eventStartTime.date()
  const dayOfWeek = eventStartTime.day()
  let dayOfWeekOutlook: DayOfWeek = 'monday'

  // map dayjs day of week to outlook day of week, so 1 = 'monday' 2 = 'tuesday' etc

  switch (dayOfWeek) {
    case 1:
      dayOfWeekOutlook = 'monday'
      break
    case 2:
      dayOfWeekOutlook = 'tuesday'
      break
    case 3:
      dayOfWeekOutlook = 'wednesday'
      break
    case 4:
      dayOfWeekOutlook = 'thursday'
      break
    case 5:
      dayOfWeekOutlook = 'friday'
      break
    case 6:
      dayOfWeekOutlook = 'saturday'
      break
    case 0:
      dayOfWeekOutlook = 'sunday'
      break
  }

  const month = eventStartTime.month() + 1
  const days: DayOfWeek[] =
    recur.Unit === RECURRENCE_UNIT.yearly ||
    recur.Unit === RECURRENCE_UNIT.monthly
      ? []
      : recur.Unit === RECURRENCE_UNIT.weekly
      ? [dayOfWeekOutlook]
      : [
          'sunday',
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
          'friday',
          'saturday'
        ]
  Logger.blue('Outlook: Days', days)
  recurrence.pattern = {
    type: type,
    month:
      recur.Unit === RECURRENCE_UNIT.yearly ||
      recur.Unit === RECURRENCE_UNIT.monthly
        ? month
        : 0,
    dayOfMonth:
      recur.Unit === RECURRENCE_UNIT.yearly ||
      recur.Unit === RECURRENCE_UNIT.monthly
        ? dayOfMonth
        : 0,
    firstDayOfWeek:
      userSettings.FirstDayOfTheWeek === 0
        ? 'sunday'
        : userSettings.FirstDayOfTheWeek === 1
        ? 'monday'
        : 'saturday',
    daysOfWeek: days,
    interval: Number(event.Recurrence?.Interval),
    index: 'first'
  }
  recurrence.range = {
    type: recur.EndDate ? 'endDate' : 'noEnd',
    startDate: dayjs.unix(Number(event.StartDate)).format('YYYY-MM-DD'),
    endDate: recur.EndDate
      ? dayjs.unix(Number(recur.EndDate)).format('YYYY-MM-DD')
      : undefined
  }

  return recurrence
}
